import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import SectionCard from "../../components/account/SectionCard";
import {
  getSettings,
  updatePreferences,
  updateGlobalSettings,
} from "../../services/notificationService";
import {
  Bell,
  Save,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import Swal from "../../utils/apparifySwal";
import { FormSkeleton } from "../../components/skeletons/ShopperSkeletons";

/* ═══════════════════════════════════════════════
   Toggle Switch component
   ═══════════════════════════════════════════════ */
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${checked
          ? "bg-neutral-900 dark:bg-neutral-100"
          : "bg-neutral-300 dark:bg-neutral-700"
        }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white dark:bg-neutral-900 transition-transform
          ${checked ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

/* ═══════════════════════════════════════════════
   Notification settings rows
   ═══════════════════════════════════════════════ */
const NOTIFICATION_ROWS = [
  {
    key: "ORDER",
    channel: "EMAIL",
    label: "Order Updates",
    desc: "Updates on shipping and delivery status of all orders",
  },
  {
    key: "PROMO",
    channel: "EMAIL",
    label: "Promotions",
    desc: "Exclusive updates on upcoming deals and campaigns",
  },
  {
    key: "ACCOUNT",
    channel: "EMAIL",
    label: "Customer Surveys",
    desc: "Receive surveys to help serve you better",
  },
];

/* ═══════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════ */
export default function NotificationSettingsPage() {
  const { isDark } = useTheme();

  const [prefs, setPrefs]           = useState([]);
  const [globalCfg, setGlobalCfg]   = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [dirty, setDirty]           = useState(false);
  const [emailMaster, setEmailMaster] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  /* ── Fetch ───────────────────────────────── */
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { preferences, global: g } = await getSettings();
      if (!mountedRef.current) return;
      setPrefs(preferences || []);
      setGlobalCfg(g || {});
      setDirty(false);

      // Derive email master toggle from email channel prefs
      const emailPrefs = (preferences || []).filter(p => p.channel === "EMAIL");
      const allDisabled = emailPrefs.length > 0 && emailPrefs.every(p => !p.enabled && !p.required);
      setEmailMaster(!allDisabled);
    } catch (err) {
      console.error('[settings] fetch error:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  /* ── Toggle a preference ─────────────────── */
  const togglePref = (category, channel) => {
    setPrefs(prev => prev.map(p =>
      p.category === category && p.channel === channel && !p.required
        ? { ...p, enabled: !p.enabled }
        : p,
    ));
    setDirty(true);
  };

  /* ── Master email toggle ─────────────────── */
  const toggleEmailMaster = (val) => {
    setEmailMaster(val);
    setPrefs(prev => prev.map(p =>
      p.channel === "EMAIL" && !p.required
        ? { ...p, enabled: val }
        : p,
    ));
    setDirty(true);
  };

  /* ── Save all ────────────────────────────── */
  const handleSave = async () => {
    setSaving(true);
    try {
      const prefPayload = prefs
        .filter(p => !p.required)
        .map(({ category, channel, enabled }) => ({ category, channel, enabled }));

      await Promise.all([
        updatePreferences(prefPayload),
        updateGlobalSettings({
          digestMode:      globalCfg.digestMode,
          quietHoursStart: globalCfg.quietHoursStart || null,
          quietHoursEnd:   globalCfg.quietHoursEnd   || null,
          timezone:        globalCfg.timezone,
          promoDailyCap:   globalCfg.promoDailyCap,
        }),
      ]);

      if (mountedRef.current) {
        setDirty(false);
        Swal.fire({ icon: "success", title: "Saved", text: "Notification settings updated.", timer: 1800, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to save settings. Please try again." });
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  /* ── Helpers ─────────────────────────────── */
  const getPref = (category, channel) =>
    prefs.find(p => p.category === category && p.channel === channel) || { enabled: false, required: false };

  /* ── Skeleton ────────────────────────────── */
  if (loading) {
    return (
      <SectionCard title="Notification Settings" isDark={isDark}>
        <FormSkeleton sections={1} label="Loading notification settings" />
      </SectionCard>
    );
  }

  /* ── Toolbar ─────────────────────────────── */
  const toolbar = (
    <div className="flex items-center gap-2">
      <Link
        to="/account/notifications"
        className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5
                   text-[11px] font-semibold tracking-wider uppercase
                   border-neutral-200 text-neutral-600 hover:bg-neutral-100
                   dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 transition"
      >
        <ArrowLeft size={13} /> Inbox
      </Link>

      <button
        onClick={handleSave}
        disabled={!dirty || saving}
        className="inline-flex items-center gap-1.5 rounded-xl px-4 py-1.5
                   text-[11px] font-semibold tracking-wider uppercase
                   bg-neutral-900 text-white hover:bg-neutral-800
                   dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200
                   disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
        Save Changes
      </button>
    </div>
  );

  return (
    <SectionCard
      title="Notification Settings"
      subtitle="Manage how and when you receive notifications"
      isDark={isDark}
      action={toolbar}
    >
      {/* ── Email Notifications Master ────── */}
      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        {/* Master toggle row */}
        <div className="flex items-center justify-between px-4 py-4 sm:px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
              <Bell size={16} className="text-neutral-600 dark:text-neutral-300" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
                Email Notifications
              </h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Important account notifications and reminders cannot be turned off
              </p>
            </div>
          </div>
          <Toggle checked={emailMaster} onChange={toggleEmailMaster} />
        </div>

        {/* Sub-toggles */}
        <div className="border-t border-neutral-100 dark:border-neutral-800">
          {NOTIFICATION_ROWS.map((row) => {
            const p = getPref(row.key, row.channel);
            const disabled = !emailMaster || !!p.required;
            const checked = emailMaster && (p.enabled || !!p.required);

            return (
              <div
                key={row.key}
                className="flex items-center justify-between px-4 py-3.5 sm:px-5 border-b border-neutral-50 dark:border-neutral-800/50 last:border-0"
              >
                <div className="pl-12">
                  <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                    {row.label}
                    {p.required && (
                      <span className="ml-1.5 text-[10px] text-neutral-400">(Required)</span>
                    )}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {row.desc}
                  </p>
                </div>
                <Toggle
                  checked={checked}
                  onChange={() => togglePref(row.key, row.channel)}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      </div>
    </SectionCard>
  );
}
